import React, { useMemo, useState } from 'react';
import { get, chain, take } from 'lodash';
import PropTypes from 'prop-types';
import { MessageBar, MessageBarType, Spinner, SpinnerSize, Text } from 'office-ui-fabric-react';
import useAPI from '../../hooks/useAPI';
import Module from '../Module';
import ModuleContent from '../ModuleContent';
import ModuleHeader from '../ModuleHeader';
import AdjustmentActivityItems from '../AdjustmentActivityItems';
import TextButton from '../TextButton';
import styles from './AdjustmentActivityList.style';
import { fetchAdjustmentActivity } from '../../actions/adjustments';

const AdjustmentActivityList = ({ adjustmentId }) => {
  const [count, setCount] = useState(3);

  if (!adjustmentId) return <Spinner size={SpinnerSize.medium} />;

  const { error, data, isLoading } = useAPI(
    () => fetchAdjustmentActivity(adjustmentId),
    [adjustmentId],
  );

  const rawActivities = get(data, 'entities.activity') || {};

  const activities = useMemo(() => {
    if (data) {
      let firstEdit = true;
      return chain(rawActivities)
        .values()
        .filter(
          activity =>
            activity.params.adjustmentId === adjustmentId || activity.resource === adjustmentId,
        )
        .orderBy(['timestamp'], ['desc'])
        .filter(activity => {
          if (activity.operation === 'edited') {
            if (!firstEdit) return false;
            firstEdit = false;
          }
          return true;
        })
        .value();
    }
    return [];
  }, [rawActivities, adjustmentId]);

  return (
    <Module>
      <ModuleHeader
        extra={
          <Text aria-label="Expanded" role="alert" as="span" className={styles.activityShowing}>
            Showing{' '}
            {activities.length > (activities.length > count ? count - 3 : count) ? (
              <span>{count}</span>
            ) : (
              <span>{activities.length}</span>
            )}{' '}
            of <span>{activities.length}</span>
          </Text>
        }
        className={styles.adjustmentActivity}
      >
        Adjustment Activity
      </ModuleHeader>
      <ModuleContent className={styles.activityContent}>
        {error && (
          <MessageBar
            isMultiline={false}
            messageBarType={MessageBarType.error}
            dismissButtonAriaLabel="Close"
          >
            {error.name}: Try reloading or contact support.
          </MessageBar>
        )}
        {isLoading && (
          <>
            <br />
            <Spinner size={SpinnerSize.medium} />
          </>
        )}
        {activities && <AdjustmentActivityItems items={take(activities, count)} />}
        {activities && activities.length > 3 && activities.length > count && (
          <div>
            <TextButton
              tabIndex="0"
              noPadding
              aria-label="Show More Activity"
              className={styles.showMoreActivityButton}
              onClick={() => setCount(count + 10)}
            >
              <Text as="p" variant="small" className={styles.showMoreActivity}>
                Show More Activity
              </Text>
            </TextButton>
          </div>
        )}
      </ModuleContent>
    </Module>
  );
};

AdjustmentActivityList.propTypes = {
  adjustmentId: PropTypes.string.isRequired,
};

AdjustmentActivityList.defaultProps = {};

export default AdjustmentActivityList;
